if (!customElements.get('e8-card-gallery')) {
  customElements.define('e8-card-gallery', class extends HTMLElement {
    connectedCallback() {
      const source = this.querySelector('[data-card-gallery-json]');
      this.image = this.querySelector('.media > img');
      this.controls = this.querySelector('[data-card-controls]');
      if (!source || !this.image || !this.controls || this.events) return;
      try { this.data = JSON.parse(source.textContent); } catch { return; }
      this.allImages = this.data.images;
      if (!Array.isArray(this.allImages) || this.allImages.length < 2) return;
      this.images = this.allImages;
      this.index = Math.max(0, this.images.findIndex(image => image.id === this.data.initial));
      for (const [color, ids] of Object.entries(this.data.groups || {})) {
        if (ids.includes(this.data.initial)) {
          this.color = color;
          this.images = ids.map(id => this.allImages.find(image => image.id === id)).filter(Boolean);
          this.index = Math.max(0, this.images.findIndex(image => image.id === this.data.initial));
          break;
        }
      }
      if (this.data.rolledFirst) {
        this.images = this.rolledFirst(this.images);
        this.index = Math.max(0, this.images.findIndex(image => image.id === this.data.initial));
      }
      this.dataset.cardView = /rolled/i.test(this.image.alt) ? 'rolled' : 'other';
      this.image.dataset.cardCurrent = '';
      this.events = new AbortController();
      const signal = this.events.signal;
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-card-step]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        event.stopPropagation();
        this.step(Number(button.dataset.cardStep));
      }, { signal });
      this.addEventListener('keydown', event => {
        if (!event.target.closest('[data-card-step]') || event.ctrlKey || event.metaKey || event.altKey) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        if (event.key === 'Home') this.show(0, -1);
        else if (event.key === 'End') this.show(this.images.length - 1, 1);
        else this.step(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      this.addEventListener('pointerenter', () => this.preloadNext(), { signal, passive: true });
      this.addEventListener('focusin', () => this.preloadNext(), { signal });
      this.classList.add('e8-card-gallery--ready');
      this.updateCount(false);
    }
    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.request = (this.request || 0) + 1;
      this.motion?.cancel();
    }
    preloadNext() {
      if (!this.images?.length) return;
      const next = this.images[(this.index + 1) % this.images.length];
      if (this.preloadSrc === next.src) return;
      this.preloadSrc = next.src;
      const image = new Image();
      image.src = next.src;
    }
    step(direction) {
      this.show((this.index + direction + this.images.length) % this.images.length, direction);
    }
    rolledFirst(images) {
      const rolled = images.find(image => /rolled/i.test(image.alt));
      return rolled ? [rolled, ...images.filter(image => image !== rolled)] : images;
    }
    selectColor(name, source) {
      const key = name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const ids = this.data.groups?.[key];
      if (ids?.length) {
        this.color = key;
        this.images = ids.map(id => this.allImages.find(image => image.id === id)).filter(Boolean);
        if (this.data.rolledFirst) this.images = this.rolledFirst(this.images);
        this.show(0, 1);
        return true;
      }
      this.images = this.allImages;
      const match = this.allImages.findIndex(image => image.src.split('?')[0] === source?.split('?')[0]);
      this.request = (this.request || 0) + 1;
      this.index = Math.max(0, match);
      this.updateCount(false);
      return false;
    }
    show(index, direction) {
      const selected = this.images[index];
      if (!selected) return;
      this.index = index;
      const request = this.request = (this.request || 0) + 1;
      this.controls.setAttribute('aria-busy', 'true');
      const preload = new Image();
      let applied = false;
      const apply = () => {
        if (applied || request !== this.request || !this.isConnected) return;
        applied = true;
        this.motion?.cancel();
        this.image.removeAttribute('srcset');
        this.image.src = selected.src;
        this.image.alt = selected.alt;
        this.dataset.cardView = /rolled/i.test(selected.alt) ? 'rolled' : 'other';
        this.image.width = selected.width;
        this.image.height = selected.height;
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.motion = this.querySelector('.media').animate([
            { transform: `translateX(${direction * 7}%)`, opacity: 0.35 },
            { transform: 'translateX(0)', opacity: 1 }
          ], { duration: 230, easing: 'cubic-bezier(.2,.65,.25,1)' });
        }
        this.controls.removeAttribute('aria-busy');
        this.updateCount(true);
      };
      preload.onload = apply;
      preload.onerror = () => {
        if (request !== this.request) return;
        this.controls.removeAttribute('aria-busy');
        this.index = this.displayedIndex || 0;
      };
      preload.src = selected.src;
      if (preload.complete && preload.naturalWidth) apply();
    }
    updateCount(announce) {
      this.displayedIndex = this.index;
      this.controls.hidden = this.images.length < 2;
      const counter = this.querySelector('[data-card-count]');
      if (counter) counter.textContent = `${this.index + 1} / ${this.images.length}`;
      if (announce) this.querySelector('[data-card-status]').textContent = this.data.status.replace('[index]', this.index + 1).replace('[total]', this.images.length);
    }
  });
}